import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Grid, Button, Heading } from '@design-systems/canvas-ui'
import { Dialog, Divider } from '@enterprise-ui/canvas-ui-react'
import { logEvent, LogLevel } from '@praxis/component-logging'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { isValidPhoneNumber } from 'libphonenumber-js'
import ConnectionResult from './ConnectionResult'
import '../css/header.css'
import '../css/GuestDetails.css'
import '../css/common.css'
import {
  clearGuestFlowSession,
  loadCaptivePortalContext,
  clearCaptivePortalContext,
  buildSuccessUrl,
  loadGuestFlowSession,
  saveGuestFlowSession,
  saveCaptivePortalContext,
  isSafeRedirectUrl,
} from '../utils/tmAuthStorage'
import {
  GUEST_BLOCK_PATH,
  GUEST_RESEND_EMAIL_PATH,
  GUEST_SESSION_PATH,
  GUEST_SPONSOR_STATUS_PATH,
  GUEST_SUBMIT_PATH,
  GUEST_UPDATE_STATUS_PATH,
} from '../utils/apiRoutes'

function normalizeMacAddress(macAddress) {
  return macAddress?.trim().toLowerCase() || null
}

const GuestDetails = () => {
  const location = useLocation()

  const OTP_WINDOW_MINUTES = 2
  const OTP_WINDOW_SECONDS = 0
  const SPONSOR_WINDOW_MINUTES = 15
  const SPONSOR_WINDOW_SECONDS = 0
  const MAX_EMAIL_RESEND_ATTEMPTS = 5

  const [emailminutes, setEmailminutes] = useState(SPONSOR_WINDOW_MINUTES)
  const [emailseconds, setEmailSeconds] = useState(SPONSOR_WINDOW_SECONDS)

  const [minutes, setMinutes] = useState(-1) // -1 means OTP timer not started
  const [seconds, setSeconds] = useState(0)

  const [phoneNumber, setPhoneNumber] = useState('')
  const [reqId, setReqId] = useState(null)
  const [name, setName] = useState('')
  const DOMAIN = '@target.com'
  const [emailPrefix, setEmailPrefix] = useState('')
  const [retrynum, setRetrynum] = useState(0)

  const [dialogblock, setDialogblock] = useState(false)
  const [blockedAt, setBlockedAt] = useState(null)
  const [dialogopen, setDialogopen] = useState(false)
  const [dialogemail, setDialogemail] = useState(false)

  const [dis, setDis] = useState(true)
  const [disPhone, setDisPhone] = useState(true)
  const [disEmail, setDisEmail] = useState(true)
  const [disCode, setDisCode] = useState(false)
  const [disresend, setDisResend] = useState(true)
  const [resendretry, setResendRetry] = useState(true)
  const [otp, setOtp] = useState(null)

  const [retry, setRetry] = useState(false)
  const [wrongNumber, setWrongNumber] = useState(false)
  const [cancelopen, setCancelopen] = useState(false)

  const [sponsorstatus, setSponsorstatus] = useState(null)
  const [checkstatus, setCheckstatus] = useState(null)
  const [pollErrorCount, setPollErrorCount] = useState(0)
  const [resendStatus, setResendStatus] = useState(null) // 'sent' | 'error' | null
  const [resendCount, setResendCount] = useState(0)
  const [emailRateLimited, setEmailRateLimited] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState('in')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [showResult, setShowResult] = useState(false)
  const [resultType, setResultType] = useState('success')
  const [resultMessage, setResultMessage] = useState('')
  const [resultActionLabel, setResultActionLabel] = useState('')

  const routeState = useMemo(() => location.state || {}, [location.state])
  const searchParams = new URLSearchParams(window.location.search)

  const storedPortalState = useMemo(() => loadCaptivePortalContext() || {}, [])

  const mac = normalizeMacAddress(
    searchParams.get('client_mac') ||
      searchParams.get('mac_address') ||
      routeState.mac ||
      storedPortalState.mac,
  )
  const successUrl =
    routeState.successUrl ||
    storedPortalState.successUrl ||
    buildSuccessUrl(
      routeState.baseGrantUrl || storedPortalState.baseGrantUrl,
      routeState.userContinueUrl || storedPortalState.userContinueUrl,
    ) ||
    null
  const baseGrantUrl =
    routeState.baseGrantUrl || storedPortalState.baseGrantUrl || null
  const userContinueUrl =
    routeState.userContinueUrl || storedPortalState.userContinueUrl || null
  const loc = routeState.loc || storedPortalState.loc
  const devicetype =
    routeState.deviceType ||
    storedPortalState.deviceType ||
    window.navigator.userAgent

  useEffect(() => {
    if (!mac || !successUrl) return

    const currentSession = loadGuestFlowSession({
      mac,
      loc,
      successUrl,
    })

    if (
      !storedPortalState.mac ||
      normalizeMacAddress(storedPortalState.mac) !== mac
    ) {
      saveCaptivePortalContext({
        ...storedPortalState,
        ...routeState,
        mac,
        loc,
        baseGrantUrl,
        userContinueUrl,
        successUrl,
        deviceType: devicetype,
      })
    }

    if (!currentSession) {
      clearGuestFlowSession()
      return
    }

    saveGuestFlowSession({
      ...currentSession,
      mac,
      loc,
      successUrl,
    })
  }, [
    baseGrantUrl,
    devicetype,
    loc,
    mac,
    routeState,
    storedPortalState,
    successUrl,
    userContinueUrl,
  ])

  const getFullEmail = () => `${emailPrefix}${DOMAIN}`

  const ensureGuestFlowToken = useCallback(async () => {
    if (!mac) {
      throw new Error('Missing MAC address in guest flow context')
    }

    const currentSession = loadGuestFlowSession({
      mac,
      loc,
      successUrl,
    })

    if (currentSession?.token) {
      return currentSession.token
    }

    const response = await fetch(GUEST_SESSION_PATH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify({
        mac_address: mac,
        location: loc,
        device_type: devicetype,
        base_grant_url: baseGrantUrl,
        user_continue_url: userContinueUrl,
        success_url: successUrl,
      }),
    })

    if (!response.ok) {
      throw new Error(`guest session failed: ${response.status}`)
    }

    const payload = await response.json()
    const token = payload?.token || payload?.guest_flow_token

    if (!token) {
      throw new Error('Guest session response missing token')
    }

    saveGuestFlowSession({
      token,
      expiresAt: payload?.expires_at || null,
      mac,
      loc,
      successUrl,
    })

    return token
  }, [baseGrantUrl, devicetype, loc, mac, successUrl, userContinueUrl])

  const fetchGuest = useCallback(
    async (url, method, body, allowRetry = true) => {
      const token = await ensureGuestFlowToken()

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'X-Guest-Flow-Token': token,
        },
        ...(body ? { body: JSON.stringify(body) } : {}),
      })

      if (allowRetry && (response.status === 401 || response.status === 403)) {
        clearGuestFlowSession()
        return fetchGuest(url, method, body, false)
      }

      return response
    },
    [ensureGuestFlowToken],
  )

  const resetGuestFlow = useCallback(() => {
    clearGuestFlowSession()
    setEmailminutes(SPONSOR_WINDOW_MINUTES)
    setEmailSeconds(SPONSOR_WINDOW_SECONDS)
    setMinutes(-1) // Reset to not started
    setSeconds(OTP_WINDOW_SECONDS)

    setPhoneNumber('')
    setReqId(null)
    setName('')
    setEmailPrefix('')
    setRetrynum(0)

    setDialogblock(false)
    setBlockedAt(null)
    setDialogopen(false)
    setDialogemail(false)

    setDis(true)
    setDisPhone(true)
    setDisEmail(true)
    setDisCode(false)
    setDisResend(true)
    setResendRetry(true)
    setOtp(null)

    setRetry(false)
    setWrongNumber(false)
    setCancelopen(false)

    setSponsorstatus(null)
    setCheckstatus(null)
    setPollErrorCount(0)
    setResendStatus(null)
    setResendCount(0)
    setEmailRateLimited(false)
    setSelectedCountry('in')
    setIsSubmitting(false)

    setShowResult(false)
    setResultType('success')
    setResultMessage('')
    setResultActionLabel('')
  }, [])

  const handleApprove = useCallback(() => {
    setDialogemail(false)
    setDialogopen(true)
    setCheckstatus(null)
    // Start the OTP timer only when sponsor approves, capped by remaining sponsor window
    const sponsorRemainingSeconds = emailminutes * 60 + emailseconds
    const otpWindowSeconds = OTP_WINDOW_MINUTES * 60 + OTP_WINDOW_SECONDS
    const allowedSeconds = Math.min(otpWindowSeconds, sponsorRemainingSeconds)
    const allowedMinutes = Math.floor(allowedSeconds / 60)
    const allowedSecs = allowedSeconds % 60
    setMinutes(allowedMinutes)
    setSeconds(allowedSecs)
  }, [emailminutes, emailseconds])

  const handleDecline = useCallback(() => {
    setCheckstatus(null)
    setDialogemail(false)
    setDialogopen(false)
    setResultType('error')
    setResultMessage('Request cancelled.')
    setResultActionLabel('Back to Guest Form')
    setShowResult(true)
  }, [])

  const SponsorStatus = useCallback(
    (reqid) => {
      fetchGuest(GUEST_SPONSOR_STATUS_PATH + reqid, 'GET')
        .then((response) => response.json())
        .then((responsedata) => {
          setPollErrorCount(0)
          setSponsorstatus(responsedata['status'])

          if (responsedata['status'] === 'Approved') {
            handleApprove()
          } else if (responsedata['status'] === 'Declined') {
            handleDecline()
          }
        })
        .catch((err) => {
          setPollErrorCount((prev) => prev + 1)
          logEvent(
            { message: 'SponsorStatus poll error', error: err?.message },
            { level: LogLevel.Error },
          )
        })
    },
    [fetchGuest, handleApprove, handleDecline],
  )

  const UpdateStatus = useCallback(
    (id, otpvalue, timeoutType) => {
      fetchGuest(GUEST_UPDATE_STATUS_PATH, 'PUT', {
        id,
        otp: otpvalue,
      })
        .then((request) => request.json())
        .then((response) => {
          const msg = response['status']

          if (msg === 'Approved') {
            setRetry(false)
            dialoghandleClose()
            setResultType('success')
            setResultMessage('')
            setResultActionLabel('Done')
            setShowResult(true)
            return
          }

          if (msg === 'Wrongotp') {
            setRetrynum((prev) => prev + 1)
            setRetry(true)
            return
          }

          if (msg === 'Blocked') {
            dialoghandleClose()
            setRetry(false)
            setDisCode(true)
            setBlockedAt(new Date())
            setResultType('error')
            setResultMessage(
              'You are blocked. Please try again after 30 minutes.',
            )
            setResultActionLabel('OK')
            setShowResult(true)
            return
          }

          if (msg === 'Rejected') {
            dialoghandleClose()
            setRetry(false)
            setResultType('error')
            setResultMessage('Request cancelled.')
            setResultActionLabel('Back to Guest Form')
            setShowResult(true)
            return
          }

          if (msg === 'Time-Out') {
            dialoghandleClose()
            setRetry(false)
            setResultType('error')
            const message =
              timeoutType === 'otp'
                ? 'OTP expired. Please start again.'
                : "Didn't receive approval from sponsor TM. Please try again."
            setResultMessage(message)
            setResultActionLabel('Back to Guest Form')
            setShowResult(true)
            return
          }

          dialoghandleClose()
          setRetry(false)
          setResultType('error')
          setResultMessage('Verification failed. Please try again.')
          setResultActionLabel('Back to Guest Form')
          setShowResult(true)
        })
        .catch((err) => {
          logEvent(
            { message: 'UpdateStatus error', error: err?.message },
            { level: LogLevel.Error },
          )
        })
    },
    [fetchGuest],
  )

  function SubmitGuestData() {
    // N-M2: log only the last MAC octet to avoid PII in telemetry
    logEvent(
      { message: `guest submit mac=...:${mac?.split(':').pop() ?? 'unknown'}` },
      { level: LogLevel.Info },
    )

    return fetchGuest(GUEST_SUBMIT_PATH, 'POST', {
      name,
      mac_address: mac,
      phone_number: phoneNumber,
      sponsor_email: getFullEmail(),
      location: loc,
      device_type: devicetype,
    })
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`submit guest failed: ${resp.status}`)
        }
        return resp.json()
      })
      .then((response) => {
        if (response['message'] === 'Invalid number') {
          HandleWrongNumber()
          return false
        }

        if (
          response['message']
            ?.toLowerCase()
            .includes('maximum number of attempts reached')
        ) {
          setEmailRateLimited(true)
          setDialogemail(false)
          setResultType('error')
          setResultMessage(
            'Maximum sponsor email attempts reached. Please try again later or use a different sponsor email.',
          )
          setResultActionLabel('Back to Guest Form')
          setShowResult(true)
          return false
        }

        setReqId(response['id'])
        return true
      })
      .catch((err) => {
        logEvent(
          { message: 'SubmitGuestData error', error: err?.message },
          { level: LogLevel.Error },
        )
        setResultType('error')
        setResultMessage(
          'Unable to reach guest Wi-Fi service. Please try again.',
        )
        setResultActionLabel('Back to Guest Form')
        setShowResult(true)
        return false
      })
  }

  function HandleWrongNumber() {
    setWrongNumber(true)
    setResultType('error')
    setResultMessage('Invalid phone number. Please check and try again.')
    setResultActionLabel('Back to Guest Form')
    setShowResult(true)
  }

  function ResendEmail() {
    if (resendCount >= MAX_EMAIL_RESEND_ATTEMPTS - 1) {
      setEmailRateLimited(true)
      return
    }

    fetchGuest(GUEST_RESEND_EMAIL_PATH, 'POST', {
      id: reqId,
    })
      .then((request) => {
        if (!request.ok) {
          throw new Error(`resend email failed: ${request.status}`)
        }
        return request.json()
      })
      .then(() => {
        setResendStatus('sent')
        setResendCount((prev) => prev + 1)
        // Auto-clear the confirmation after 4 seconds
        setTimeout(() => setResendStatus(null), 4000)
      })
      .catch((err) => {
        setResendStatus('error')
        logEvent(
          { message: 'ResendEmail error', error: err?.message },
          { level: LogLevel.Error },
        )
        setTimeout(() => setResendStatus(null), 4000)
      })
  }

  function BlockUser(phone_number) {
    return fetchGuest(GUEST_BLOCK_PATH, 'PUT', {
      mac_address: mac,
      phone_number,
    })
      .then((request) => {
        if (!request.ok) {
          throw new Error(`block user failed: ${request.status}`)
        }
        return request.json()
      })
      .then((response) => {
        return response['message']
      })
  }

  const Timeout = useCallback(
    (timeoutType = 'email') => {
      if (!reqId) return
      UpdateStatus(reqId, '', timeoutType)
      dialoghandleClose()
    },
    [reqId, UpdateStatus],
  )

  useEffect(() => {
    const myInterval = setInterval(() => {
      if (emailseconds > 0) {
        setEmailSeconds(emailseconds - 1)

        if (reqId !== null && checkstatus !== null) {
          SponsorStatus(reqId)
        }
      }

      if (emailseconds === 0) {
        if (emailminutes === 0) {
          Timeout('email')
          clearInterval(myInterval)
        } else {
          setEmailminutes(emailminutes - 1)
          setEmailSeconds(59)
        }
      }

      if (emailminutes <= 5 && resendretry === true) {
        setDisResend(false)
      }
    }, 1000)

    return () => clearInterval(myInterval)
  }, [
    emailminutes,
    emailseconds,
    reqId,
    checkstatus,
    SponsorStatus,
    resendretry,
    Timeout,
    pollErrorCount,
  ])

  useEffect(() => {
    // Only run OTP timer if it has been started (i.e., sponsor has approved)
    if (minutes < 0) return

    const myInterval1 = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1)
      }

      if (seconds === 0) {
        if (minutes === 0) {
          Timeout('otp')
          clearInterval(myInterval1)
        } else {
          setMinutes(minutes - 1)
          setSeconds(59)
        }
      }
    }, 1000)

    return () => clearInterval(myInterval1)
  }, [minutes, seconds, Timeout])

  const handleOtpChange = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
    setOtp(value)
  }

  const handlePhone = (value, countryData) => {
    setDis(true)
    setDisEmail(true)

    const phn = '+' + value
    setPhoneNumber(phn)

    if (countryData?.countryCode) {
      setSelectedCountry(countryData.countryCode.toLowerCase())
    }

    const isValidPhone = isValidPhoneNumber(phn)
    setDisEmail(!isValidPhone)

    if (name.length >= 3 && emailPrefix.length >= 3 && isValidPhone) {
      setDis(false)
    }
  }

  const handleName = (e) => {
    const sanitized = e.target.value.replace(/[^A-Za-z0-9_\- ]/g, '')

    setName(sanitized)

    if (sanitized.length >= 3) {
      setDisPhone(false)
    } else {
      setDisPhone(true)
      setDisEmail(true)
      setDis(true)
      return
    }

    const isValidPhone = isValidPhoneNumber(phoneNumber)
    if (isValidPhone) {
      setDisEmail(false)
    }

    if (sanitized.length >= 3 && emailPrefix.length >= 3 && isValidPhone) {
      setDis(false)
    } else {
      setDis(true)
    }
  }

  const handleEmail = (e) => {
    setDis(true)

    let value = e.target.value

    if (value.includes(DOMAIN)) {
      value = value.replace(DOMAIN, '')
    }

    // Store original cursor position before filtering
    const cursorPosition = e.target.selectionStart
    const originalLength = value.length

    // Remove invalid characters (including spaces)
    value = value.replace(/[^A-Za-z0-9._-]/g, '')
    value = value.replace(/^[._-]+/, '')
    value = value.slice(0, 30)

    setEmailPrefix(value)

    // Restore cursor position after filtering
    const newLength = value.length
    const lengthDifference = originalLength - newLength
    const newCursorPosition = Math.max(0, cursorPosition - lengthDifference)

    // Use setTimeout to ensure the value update happens first
    setTimeout(() => {
      e.target.setSelectionRange(newCursorPosition, newCursorPosition)
    }, 0)

    const isValidPhone = isValidPhoneNumber(phoneNumber)
    if (value.length >= 3 && name.length >= 3 && isValidPhone) {
      setDis(false)
    }
  }

  const submitClicked = () => {
    if (isSubmitting) return

    if (!isValidPhoneNumber(phoneNumber)) {
      HandleWrongNumber()
      return
    }

    if (!mac) {
      setResultType('error')
      setResultMessage(
        'Unable to detect device MAC address from the captive portal. Please reconnect to Wi-Fi and try again.',
      )
      setResultActionLabel('Back to Guest Form')
      setShowResult(true)
      return
    }

    if (!successUrl) {
      setResultType('error')
      setResultMessage(
        'Unable to build captive portal success redirect. Please reconnect to Wi-Fi and try again.',
      )
      setResultActionLabel('Back to Guest Form')
      setShowResult(true)
      return
    }

    setIsSubmitting(true)
    BlockUser(phoneNumber)
      .then((isBlocked) => {
        if (isBlocked === true) {
          setDialogblock(true)
          return false
        }

        // Don't start OTP timer here - only start when sponsor approves
        setCheckstatus('check')
        setEmailminutes(SPONSOR_WINDOW_MINUTES)
        setEmailSeconds(SPONSOR_WINDOW_SECONDS)

        return SubmitGuestData()
      })
      .then((ok) => {
        if (ok) {
          setDialogemail(true)
        }
      })
      .catch((err) => {
        logEvent(
          { message: 'submitClicked error', error: err?.message },
          { level: LogLevel.Error },
        )
        setResultType('error')
        setResultMessage(
          'Unable to reach guest Wi-Fi service. Please try again.',
        )
        setResultActionLabel('Back to Guest Form')
        setShowResult(true)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleEmailPopup = () => {
    setDialogemail(false)
    setSponsorstatus(null)
    setReqId(null)
    setEmailPrefix('')
    setEmailminutes(SPONSOR_WINDOW_MINUTES)
    setEmailSeconds(SPONSOR_WINDOW_SECONDS)
    setDisResend(true)
    setResendRetry(true)
  }

  const handleClick = () => {
    UpdateStatus(reqId, otp, undefined)
  }

  const handleReject = () => {
    UpdateStatus(reqId, '0', undefined)
    dialoghandleClose()
  }

  const handleResend = () => {
    setDisResend(true)
    ResendEmail()
    if (resendCount < MAX_EMAIL_RESEND_ATTEMPTS - 1) {
      setEmailminutes(SPONSOR_WINDOW_MINUTES)
      setEmailSeconds(SPONSOR_WINDOW_SECONDS)
    }
    setResendRetry(false)
  }

  const dialoghandleClose = () => {
    setDialogopen(false)
    setCancelopen(false)
    setOtp(null)
  }

  const dialogblockClose = () => {
    resetGuestFlow()
  }

  const handleResultAction = (action) => {
    if (action === 'done') {
      if (!successUrl) {
        setResultType('error')
        setResultMessage(
          'Unable to complete captive portal redirect. Please reconnect to Wi-Fi and try again.',
        )
        setResultActionLabel('Back to Guest Form')
        setShowResult(true)
        return
      }

      // N-H1: re-validate successUrl loaded from storage before redirecting
      if (!isSafeRedirectUrl(successUrl)) {
        logEvent(
          { message: `Blocked unsafe redirect target: ${successUrl}` },
          { level: LogLevel.Error },
        )
        setResultType('error')
        setResultMessage(
          'Redirect target is not allowed. Please reconnect to Wi-Fi and try again.',
        )
        setResultActionLabel('Back to Guest Form')
        setShowResult(true)
        return
      }

      clearCaptivePortalContext()
      clearGuestFlowSession()
      window.location.replace(successUrl)
      return
    }

    resetGuestFlow()
  }

  if (showResult) {
    return (
      <ConnectionResult
        isSuccess={resultType === 'success'}
        flowType="guest"
        message={resultMessage}
        actionLabel={resultActionLabel}
        onDone={() => handleResultAction('done')}
        onRetry={() => handleResultAction('retry')}
      />
    )
  }

  return (
    <React.Fragment>
      {dialogblock && (
        <div className="modal-overlay">
          <div className="modal-content block-user-modal">
            <h2 className="block-user-modal__header">
              Access Temporarily Restricted
            </h2>

            <div className="block-user-modal__content">
              You are blocked for:
              <div className="block-user-modal__phone">{phoneNumber}</div>
              For security reasons, access has been temporarily restricted.
              <br />
              Blocked at{' '}
              {blockedAt
                ? blockedAt.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'this time'}
              . Please try again after 30 minutes from that time.
            </div>

            <Button onClick={dialogblockClose} className="btn-primary">
              OK
            </Button>
          </div>
        </div>
      )}

      <div className="guest-details-container">
        <div className="guest-details__form">
          <Grid.Container>
            <Grid.Item xs={12}>
              <span className="guest-details__header">Guest Details</span>
            </Grid.Item>
            <Grid.Item xs={12}>
              <Divider />
            </Grid.Item>
          </Grid.Container>

          <Grid.Container direction="column">
            <Grid.Item className="guest-details__field">
              <Heading className="guest-details__label">
                Enter your Name:
              </Heading>
              <input
                type="text"
                maxLength={25}
                value={name}
                placeholder="Limit 3-25 characters"
                onChange={handleName}
                className="guest-details__input"
              />
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <Heading className="guest-details__label">
                Enter your Phone Number:
              </Heading>
              <PhoneInput
                disabled={disPhone}
                country={selectedCountry || 'in'}
                value={phoneNumber}
                enableSearch
                onChange={handlePhone}
                rules={{ validate: isValidPhoneNumber }}
              />
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <Heading className="guest-details__label">
                Enter your sponsor's Target Email ID:
              </Heading>
              <input
                disabled={disEmail}
                type="text"
                value={emailPrefix + DOMAIN}
                onChange={handleEmail}
                onFocus={(e) => {
                  const prefixLength = emailPrefix.length
                  setTimeout(() => {
                    e.target.setSelectionRange(prefixLength, prefixLength)
                  }, 0)
                }}
                onClick={(e) => {
                  const prefixLength = emailPrefix.length
                  if (e.target.selectionStart > prefixLength) {
                    e.target.setSelectionRange(prefixLength, prefixLength)
                  }
                }}
                className="guest-details__email-input"
              />

              <div className="guest-details__note">
                *Ensure that you've entered the correct sponsor email ID before
                proceeding further*
              </div>
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <div className="guest-details__info">
                Target will send a one-time passcode (OTP) to the provided
                mobile number post sponsor approval. Message and data rates may
                apply.
              </div>
            </Grid.Item>

            <Grid.Item className="guest-details__field">
              <button
                disabled={dis || isSubmitting}
                onClick={submitClicked}
                className="guest-details__submit-btn"
              >
                {isSubmitting ? 'Sending...' : 'Next'}
              </button>
            </Grid.Item>
          </Grid.Container>
        </div>

        {dialogemail && (
          <div className="modal-overlay">
            <div className="modal-content email-approval-modal">
              <h2 className="email-approval-modal__header">Sponsor Approval</h2>

              <div className="email-approval-modal__content">
                An Email has been sent to your sponsor mail{' '}
                <strong>{emailPrefix + DOMAIN}</strong> for approval.
              </div>

              <div>
                {emailminutes === 0 && emailseconds === 0 ? (
                  <div>
                    {resendretry ? (
                      <p>
                        Email Expired, Didn't receive approval from sponsor TM!
                        Please recheck if you have entered correct sponsor mail
                        id or not.
                        <br />
                        Click Resend to resend the mail to the same sponsor else
                        click Edit to change the sponsor.
                      </p>
                    ) : (
                      <p>
                        Email Expired, Didn't receive approval from sponsor TM!
                        <br />
                        Please recheck if you have entered correct sponsor mail
                        id or not.
                        <br />
                        Click Edit to change the sponsor mail.
                      </p>
                    )}
                  </div>
                ) : sponsorstatus !== 'Approved' &&
                  sponsorstatus !== 'Declined' ? (
                  <div className="email-approval-modal__timer">
                    Valid for {emailminutes}:
                    {emailseconds < 10 ? `0${emailseconds}` : emailseconds}
                  </div>
                ) : (
                  <h4>Your sponsor has {sponsorstatus} your request.</h4>
                )}
              </div>

              <div className="email-approval-modal__actions">
                <Button
                  disabled={disresend || emailRateLimited}
                  onClick={handleResend}
                  className="btn-secondary"
                >
                  Resend
                </Button>
                <Button onClick={handleEmailPopup} className="btn-primary">
                  Edit
                </Button>
              </div>

              {/* M5: resend confirmation / error feedback */}
              {resendStatus === 'sent' && (
                <div className="resend-status resend-status--success">
                  ✓ Email resent successfully.
                </div>
              )}
              {resendStatus === 'error' && (
                <div className="resend-status resend-status--error">
                  Failed to resend email. Please try again.
                </div>
              )}

              {emailRateLimited && (
                <div className="resend-status resend-status--error">
                  Maximum sponsor email attempts reached. Please try again later
                  or use a different sponsor email.
                </div>
              )}

              {/* N-M3: inline warning when sponsor status polling fails repeatedly */}
              {pollErrorCount >= 3 && (
                <div className="poll-error-warning">
                  Unable to reach the approval service. Please check your
                  connection. Retrying automatically…
                </div>
              )}
            </div>
          </div>
        )}

        {dialogopen && (
          <div className="modal-overlay">
            <div className="modal-content otp-modal">
              <h2 className="otp-modal__header">Verify Code</h2>

              {sponsorstatus === 'Approved' && (
                <div className="otp-modal__content">
                  Your sponsor has approved your request.
                </div>
              )}

              <div className="otp-modal__content">
                Enter the code sent to your mobile number {phoneNumber}.
              </div>

              <input
                disabled={disCode}
                maxLength={6}
                value={otp || ''}
                onChange={handleOtpChange}
                placeholder="Enter 6-digit OTP"
                className="otp-modal__input"
                autoFocus
              />

              {retry && <h4>Incorrect OTP!</h4>}

              <div className="otp-modal__timer">
                {(minutes === 0 && seconds === 0) || retrynum >= 3 ? (
                  <span>Code Expired!</span>
                ) : minutes >= 0 ? (
                  <span>
                    Code valid for {minutes}:
                    {seconds < 10 ? `0${seconds}` : seconds}
                  </span>
                ) : (
                  <span>Waiting for OTP...</span>
                )}
              </div>

              <div>
                {retrynum < 2 ? (
                  <h4>You have {2 - retrynum} retry attempts left!</h4>
                ) : (
                  <h4>
                    One more incorrect OTP will block this guest for 30 minutes.
                  </h4>
                )}
              </div>

              {wrongNumber && (
                <h4 style={{ color: 'red' }}>Invalid Phone Number</h4>
              )}

              <div className="otp-modal__actions">
                <Button
                  disabled={cancelopen}
                  onClick={handleReject}
                  className="btn-secondary"
                >
                  Cancel
                </Button>
                <Button
                  disabled={cancelopen || !otp || otp.length < 6}
                  onClick={handleClick}
                  className="btn-primary"
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        )}

        <Dialog
          headingText="RESTRICTED"
          bodyText="Not accessible outside Target Network"
          isVisible={false}
        />
      </div>
    </React.Fragment>
  )
}

export default GuestDetails
